/**
 * Electricity flow: glowing sparks that travel through every part, driven by the
 * simulation's real currents.
 *
 *  - Direction = the direction current actually flows (from + around to −).
 *  - Speed and brightness grow with the amount of current (log scale, so a tiny LED trickle
 *    and a big motor rush are both visible and clearly different).
 *  - Parts with no current (a dead end, an open switch) have no sparks at all.
 *  - A short circuit turns the sparks hot red and very fast.
 */
import * as THREE from 'three';
import { glowTexture } from './kit';

const SPACING = 0.3;
const MAX_POINTS = 4000;
/** Below this many amps, nothing flows (just leakage in the math). */
const MIN_AMPS = 2e-4;

export interface FlowSource {
  id: string;
  curve: THREE.Curve<THREE.Vector3>;
  matrix: THREE.Matrix4;
  /** Amps through the part, pin 0 → pin 1. */
  current: number;
}

const COOL = new THREE.Color('#ffe14d');
const WARM = new THREE.Color('#7df3ff');
const HOT = new THREE.Color('#ff4b2b');

/** 0…1 "how much" for a current, on a log scale from 1 mA to 6 A. */
export function flowAmount(amps: number): number {
  const a = Math.abs(amps);
  if (a < MIN_AMPS) return 0;
  return Math.min(1, Math.log10(1 + a * 1000) / Math.log10(1 + 6000));
}

export class Flow {
  readonly points: THREE.Points;
  private positions = new Float32Array(MAX_POINTS * 3);
  private colors = new Float32Array(MAX_POINTS * 3);
  private phase = new Map<string, number>();
  private lengths = new WeakMap<THREE.Curve<THREE.Vector3>, number>();
  private material: THREE.PointsMaterial;
  /** 0…1 fade, so turning flow on/off is smooth. */
  private fade = 0;
  enabled = true;
  /** X-ray: bigger, brighter sparks. */
  boost = 0;

  constructor() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geo.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);
    this.material = new THREE.PointsMaterial({
      size: 0.62,
      map: glowTexture(),
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    this.points = new THREE.Points(geo, this.material);
    this.points.renderOrder = 20;
    this.points.frustumCulled = false;
  }

  update(sources: FlowSource[], dt: number) {
    this.fade += ((this.enabled ? 1 : 0) - this.fade) * (1 - Math.exp(-8 * dt));
    this.material.opacity = this.fade;
    this.material.size = 0.62 + this.boost * 0.25;
    let n = 0;
    const tmp = new THREE.Vector3();
    const col = new THREE.Color();
    const live = new Set<string>();
    if (this.fade > 0.01)
      for (const s of sources) {
        const amount = flowAmount(s.current);
        if (!amount) continue;
        live.add(s.id);
        let len = this.lengths.get(s.curve);
        if (len === undefined) {
          len = s.curve.getLength();
          this.lengths.set(s.curve, len);
        }
        if (len < 0.05) continue;
        const short = Math.abs(s.current) > 1.5;
        const speed = 0.35 + amount * 4.2; // studs per second
        const dir = Math.sign(s.current);
        let ph = (this.phase.get(s.id) ?? 0) + (dir * speed * dt) / len;
        ph -= Math.floor(ph);
        this.phase.set(s.id, ph);
        const count = Math.max(2, Math.round(len / SPACING));
        // Color: soft yellow for a trickle, bright cyan-white for a rush, red for a short.
        if (short) col.copy(HOT);
        else col.copy(COOL).lerp(WARM, amount * 0.6);
        const bright = 0.7 + amount * 0.6 + this.boost * 0.3;
        for (let i = 0; i < count && n < MAX_POINTS; i++) {
          const u = (i / count + ph) % 1;
          s.curve.getPointAt(u, tmp).applyMatrix4(s.matrix);
          this.positions[n * 3] = tmp.x;
          this.positions[n * 3 + 1] = tmp.y;
          this.positions[n * 3 + 2] = tmp.z;
          // Sparks swell gently as they travel, so the stream looks alive.
          const pulse = 0.8 + 0.2 * Math.sin((u * count + ph) * Math.PI * 2);
          this.colors[n * 3] = col.r * bright * pulse;
          this.colors[n * 3 + 1] = col.g * bright * pulse;
          this.colors[n * 3 + 2] = col.b * bright * pulse;
          n++;
        }
      }
    for (const id of this.phase.keys()) if (!live.has(id)) this.phase.delete(id);
    const geo = this.points.geometry;
    geo.setDrawRange(0, n);
    (geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (geo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
  }
}
